import { createContext, useContext, useReducer, useMemo, useCallback, ReactNode } from 'react';
import { CalculationRuleSummandInput } from 'entities/tag';

/**
 * F6 — single owning store for the calculation-rule "pick terms" flow.
 *
 * Previously the pick state was split across TagContext (`calcPickMode`), a
 * `useRef` in TagInfo (`summandsByConcept`, desynced from the context Sets), and
 * react-query on commit — which made undo/cancel impossible. This consolidates the
 * whole draft (rule id, selected term concepts, picked tag ids, and the committable
 * summands) into one reducer with a history stack, committing only when TagInfo saves.
 */
export interface CalcPickDraft {
  ruleId: string | null;
  parentConceptId: string;
  parentConceptName: string;
  parentTagId: string;
  termConceptIds: Set<string>;
  pickedTagIds: Set<string>;
  summandsByConcept: Map<string, CalculationRuleSummandInput>;
}

interface CalcPickState {
  draft: CalcPickDraft | null; // null ⇒ not in pick mode
  history: CalcPickDraft[]; // snapshots for undo
}

export type CalcPickAction =
  | { type: 'ENTER'; draft: CalcPickDraft }
  | { type: 'TOGGLE_TERM'; conceptId: string; tagId?: string }
  | { type: 'UNDO' }
  | { type: 'CANCEL' };

const cloneDraft = (d: CalcPickDraft): CalcPickDraft => ({
  ...d,
  termConceptIds: new Set(d.termConceptIds),
  pickedTagIds: new Set(d.pickedTagIds),
  summandsByConcept: new Map(d.summandsByConcept),
});

export function calcPickReducer(state: CalcPickState, action: CalcPickAction): CalcPickState {
  switch (action.type) {
    case 'ENTER':
      return { draft: action.draft, history: [] };
    case 'CANCEL':
      return { draft: null, history: [] };
    case 'UNDO': {
      if (!state.history.length) return state;
      return { draft: state.history[state.history.length - 1], history: state.history.slice(0, -1) };
    }
    case 'TOGGLE_TERM': {
      if (!state.draft) return state;
      const next = cloneDraft(state.draft);
      const { conceptId, tagId } = action;
      if (next.termConceptIds.has(conceptId)) {
        next.termConceptIds.delete(conceptId);
        if (tagId) next.pickedTagIds.delete(tagId);
        // keep the summandsByConcept entry so re-selecting restores weight/dimQualifiers
      } else {
        next.termConceptIds.add(conceptId);
        if (tagId) next.pickedTagIds.add(tagId);
        if (!next.summandsByConcept.has(conceptId)) next.summandsByConcept.set(conceptId, { summandConceptId: conceptId });
      }
      return { draft: next, history: [...state.history, state.draft] };
    }
    default:
      return state;
  }
}

interface CalcPickContextValue {
  /** The active pick draft, or null when not picking. Read by TagInfo + Slate consumers. */
  calcPickMode: CalcPickDraft | null;
  canUndo: boolean;
  enterPick: (draft: CalcPickDraft) => void;
  toggleTerm: (conceptId: string, tagId?: string) => void;
  undo: () => void;
  cancel: () => void;
}

const CalcPickContext = createContext<CalcPickContextValue>({} as CalcPickContextValue);

export const CalcPickProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(calcPickReducer, { draft: null, history: [] });

  const enterPick = useCallback((draft: CalcPickDraft) => dispatch({ type: 'ENTER', draft }), []);
  const toggleTerm = useCallback((conceptId: string, tagId?: string) => dispatch({ type: 'TOGGLE_TERM', conceptId, tagId }), []);
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const cancel = useCallback(() => dispatch({ type: 'CANCEL' }), []);

  const value = useMemo<CalcPickContextValue>(
    () => ({ calcPickMode: state.draft, canUndo: state.history.length > 0, enterPick, toggleTerm, undo, cancel }),
    [state.draft, state.history.length, enterPick, toggleTerm, undo, cancel]
  );

  return <CalcPickContext.Provider value={value}>{children}</CalcPickContext.Provider>;
};

export const useCalcPick = () => useContext(CalcPickContext);
